import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { sitesApi } from '@/services/config';
import {
  contentService,
  type GenerateResult,
  type SourceType,
  type WpSeoPayload,
} from '@/services/content';

import styles from './FrammingModal.module.css';

export function FrammingModal({
  open,
  onClose,
  sourceType,
  tema,
  payload,
  onGenerated,
}: {
  open: boolean;
  onClose: () => void;
  sourceType: SourceType;
  tema: string;
  payload: Record<string, unknown>;
  onGenerated?: (r: GenerateResult) => void;
}) {
  const [framming, setFramming] = useState('');
  const [dest, setDest] = useState('');
  const [imagemIa, setImagemIa] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);

  const sitesQuery = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => sitesApi.list(true),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const sites = sitesQuery.data ?? [];
  const hasDest = Boolean(dest);

  // Default: ao carregar os sites, já seleciona o primeiro destino (evita o erro
  // "Nenhum destino configurado" por esquecer de escolher). O usuário pode trocar.
  useEffect(() => {
    if (!dest && sites.length > 0) setDest(sites[0].slug);
  }, [sites, dest]);

  const run = useMutation({
    mutationFn: (vars: { dry_run: boolean; status?: 'draft' | 'publish' }) =>
      contentService.generate({
        source_type: sourceType,
        payload: { ...payload, tema, imagem_ia: imagemIa },
        framming: framming.trim(),
        status: vars.status ?? 'draft',
        wp_site: dest || undefined,
        dry_run: vars.dry_run,
      }),
    onSuccess: (r) => {
      setResult(r);
      if (!r.dry_run) onGenerated?.(r);
    },
  });

  // G4: promove a prévia já gerada (sem novo LLM).
  const savePrev = useMutation({
    mutationFn: (status: 'draft' | 'publish') => {
      if (!result) throw new Error('sem prévia');
      return contentService.savePreview({
        source_type: sourceType,
        tema,
        editoria: typeof payload.editoria === 'string' ? payload.editoria : undefined,
        title: result.title,
        excerpt: result.excerpt,
        content_html: result.content_html ?? '',
        cover_image_url: result.cover_image_url ?? undefined,
        keywords: result.seo?.keywords,
        framming: framming.trim(),
        wp_site: dest || undefined,
        status,
      });
    },
    onSuccess: (r) => {
      setResult(r);
      onGenerated?.(r);
    },
  });

  const errMsg = run.error
    ? (run.error instanceof ApiError &&
        (run.error.body as { detail?: string } | null)?.detail) ||
      'Falha ao gerar. Tente novamente.'
    : '';

  if (!open) return null;

  const close = () => {
    setFramming('');
    setDest('');
    setImagemIa(false);
    setResult(null);
    run.reset();
    onClose();
  };

  return (
    <div className={styles.backdrop} onClick={close}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div>
            <h2 className={styles.title}>Gerar com ângulo</h2>
            <p className={styles.tema}>{tema}</p>
          </div>
          <button className={styles.x} onClick={close} aria-label="Fechar">×</button>
        </header>

        <label className={styles.label}>
          Framming (ângulo editorial) — opcional
          <textarea
            className={styles.textarea}
            value={framming}
            onChange={(e) => setFramming(e.target.value)}
            placeholder="Ex.: foque no impacto para o bolso do trabalhador; tom analítico; conecte ao cenário local…"
            rows={3}
          />
          <span className={styles.hint}>
            Vazio = matéria neutra. Preenchido = a IA escreve sob essa ótica,
            mantendo a factualidade.
          </span>
        </label>

        <label className={styles.label}>
          Destino (site WordPress)
          {sites.length > 0 ? (
            <select
              className={styles.select}
              value={dest}
              onChange={(e) => setDest(e.target.value)}
            >
              <option value="">— escolha o destino —</option>
              {sites.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.warn}>
              Nenhum destino configurado. Cadastre um site em <b>Config → Sites</b> para
              poder gerar rascunho. (A pré-visualização funciona sem destino.)
            </span>
          )}
        </label>

        <label className={styles.checkRow}>
          <input
            type="checkbox"
            checked={imagemIa}
            onChange={(e) => setImagemIa(e.target.checked)}
          />
          <span>
            Gerar capa por IA se a fonte não tiver imagem
            <small className={styles.hint}> (custa ~US$0,04; por padrão usa a foto da fonte)</small>
          </span>
        </label>

        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => run.mutate({ dry_run: true })}
            loading={run.isPending && run.variables?.dry_run === true}
          >
            Pré-visualizar
          </Button>
          <Button
            variant="secondary"
            onClick={() => run.mutate({ dry_run: false, status: 'draft' })}
            loading={run.isPending && run.variables?.dry_run === false && run.variables?.status === 'draft'}
            disabled={!hasDest}
            title={hasDest ? '' : 'Escolha um destino primeiro'}
          >
            Salvar rascunho
          </Button>
          <Button
            onClick={() => {
              if (window.confirm('Publicar ao vivo no site agora?'))
                run.mutate({ dry_run: false, status: 'publish' });
            }}
            loading={run.isPending && run.variables?.status === 'publish'}
            disabled={!hasDest}
            title={hasDest ? '' : 'Escolha um destino primeiro'}
          >
            Publicar ao vivo
          </Button>
        </div>

        {errMsg && <p className={styles.err}>{errMsg}</p>}

        {result && (
          <div className={styles.result}>
            <div className={styles.resultHead}>
              <span className={styles.badge}>
                {result.dry_run ? 'PRÉVIA' : result.status === 'publish' ? 'PUBLICADO' : 'RASCUNHO'}
              </span>
              <strong>{result.title}</strong>
              <span className={styles.sources}>
                {result.num_sources} fontes
                {typeof result.scraped === 'number' ? ` · ${result.scraped} com texto` : ''}
              </span>
            </div>
            {result.cover_image_url && (
              <div className={styles.coverRow}>
                <img className={styles.coverThumb} src={result.cover_image_url} alt="capa" />
                <span className={styles.coverCap}>
                  capa da fonte-âncora{result.cover_source ? ` · ${result.cover_source}` : ''}
                </span>
              </div>
            )}
            {result.dry_run && !result.cover_image_url && (
              <p className={styles.coverCap}>
                Sem foto na fonte-âncora — sai sem capa{imagemIa ? ' (IA tentará na publicação)' : ''}.
              </p>
            )}
            {result.excerpt && <p className={styles.excerpt}>{result.excerpt}</p>}
            {result.seo && <SeoSummary seo={result.seo} />}
            {result.dry_run && result.content_html && (
              <div
                className={styles.preview}
                dangerouslySetInnerHTML={{ __html: result.content_html }}
              />
            )}
            {result.dry_run && (
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => savePrev.mutate('draft')}
                  loading={savePrev.isPending && savePrev.variables === 'draft'}
                  disabled={!hasDest}
                  title={hasDest ? 'Salva esta prévia como rascunho — sem gerar de novo' : 'Escolha um destino primeiro'}
                >
                  Salvar esta prévia (rascunho)
                </Button>
                <Button
                  onClick={() => {
                    if (window.confirm('Publicar esta prévia ao vivo no site agora?'))
                      savePrev.mutate('publish');
                  }}
                  loading={savePrev.isPending && savePrev.variables === 'publish'}
                  disabled={!hasDest}
                  title={hasDest ? 'Publica esta prévia — sem gerar de novo' : 'Escolha um destino primeiro'}
                >
                  Publicar esta prévia
                </Button>
              </div>
            )}
            {!result.dry_run && result.wp_link && (
              <a className={styles.link} href={result.wp_link} target="_blank" rel="noreferrer">
                abrir no WordPress #{result.wp_id}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SeoSummary({ seo }: { seo: WpSeoPayload }) {
  const faqCount = Array.isArray((seo.schema.faq as { mainEntity?: unknown[] } | null)?.mainEntity)
    ? ((seo.schema.faq as { mainEntity: unknown[] }).mainEntity.length)
    : 0;
  return (
    <div className={styles.seoBox}>
      <div className={styles.seoHead}>
        <span className={styles.seoTitle}>SEO / Schema — pronto pro WP</span>
        <span className={seo.wordcount_ok ? styles.okBadge : styles.warnBadge}>
          {seo.word_count} palavras
        </span>
      </div>

      <div className={styles.seoGrid}>
        <SeoField label="Chapéu">{seo.chapeu || '—'}</SeoField>
        <SeoField label="Linha fina">{seo.linha_fina || '—'}</SeoField>
        <SeoField label="Editoria">
          {seo.editoria.name || '—'}
          {seo.editoria.id != null && <small> · #{seo.editoria.id}</small>}
        </SeoField>
        <SeoField label="Autor">
          {seo.autor.name || '—'}
          {seo.autor.id != null && <small> · #{seo.autor.id}</small>}
        </SeoField>
        <SeoField label="Créditos da imagem">
          {seo.image.credits || '— (não encontrados)'}
        </SeoField>
        <SeoField label="Seção">{seo.secao.name}</SeoField>
      </div>

      {seo.keywords.length > 0 && (
        <div className={styles.seoKw}>
          <span className={styles.seoLabel}>Palavras-chave → tags</span>
          <div className={styles.chips}>
            {seo.keywords.map((k) => (
              <span key={k} className={styles.chip}>{k}</span>
            ))}
          </div>
        </div>
      )}

      {seo.meta && Object.keys(seo.meta).length > 0 && (
        <div className={styles.seoKw}>
          <span className={styles.seoLabel}>Campos custom → WP (meta)</span>
          {Object.entries(seo.meta).map(([k, v]) => (
            <SeoField key={k} label={k}>{v || '—'}</SeoField>
          ))}
        </div>
      )}

      <details className={styles.schemaDetails}>
        <summary>NewsArticle JSON-LD (compliance Google)</summary>
        <pre className={styles.schemaPre}>
          {JSON.stringify(seo.schema.news_article, null, 2)}
        </pre>
      </details>
      {seo.schema.faq && (
        <details className={styles.schemaDetails}>
          <summary>FAQPage JSON-LD ({faqCount} perguntas)</summary>
          <pre className={styles.schemaPre}>{JSON.stringify(seo.schema.faq, null, 2)}</pre>
        </details>
      )}
      <p className={styles.seoNote}>
        O JSON-LD vai embutido no conteúdo (testável no Rich Results Test). Mapear
        chapéu/linha-fina/schema nos campos do tema fica a cargo do admin (config por WP).
      </p>
    </div>
  );
}

function SeoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.seoField}>
      <span className={styles.seoLabel}>{label}</span>
      <span className={styles.seoValue}>{children}</span>
    </div>
  );
}
